import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { Box, Button, Typography, Link } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { usePortalName } from '../../../hooks/usePortalName';

import userService from '../../../services/userService';
import securityUtils from '../../../services/securityUtils';
import authService from '../../../services/authService';
import {
  resetUserState,
  selectLoggedInUserInfo,
} from '../../../data/user.slice';

const PREFIX = 'VerifyEmailPageForm';

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
  next: `${PREFIX}-next`,
  formContainer: `${PREFIX}-formContainer`,
  largeIcon: `${PREFIX}-largeIcon`,
  formPage: `${PREFIX}-formPage`,
  inlineError: `${PREFIX}-inlineError`,
  link: `${PREFIX}-link`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    backgroundColor: 'white',
    padding: theme.spacing(4),
    paddingTop: 0,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[5],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },

  [`& .${classes.title}`]: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(4),
  },

  [`& .${classes.subtitle}`]: {
    textAlign: 'start',
    marginBottom: theme.spacing(2),
    width: '100%',
  },

  [`& .${classes.next}`]: {
    width: '100%',
    marginTop: theme.spacing(3),
  },

  [`& .${classes.largeIcon}`]: {
    display: 'block',
    margin: 'auto',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(-5),
    marginTop: theme.spacing(4),
    color: theme.palette.action.active,
  },

  [`& .${classes.formPage}`]: {
    width: '100%',
  },
  [`& .${classes.inlineError}`]: {
    marginTop: theme.spacing(-2),
    paddingLeft: theme.spacing(0.5),
    fontSize: 14,
    width: '100%',
  },
  [`& .${classes.link}`]: {
    marginTop: theme.spacing(3),
    color: theme.palette.primary.main,
    textDecoration: 'none',
    cursor: 'pointer',
    fontSize: 16,
    fontWeight: 400,
  },
}));

const StyledBox = styled(Box)({
  whiteSpace: 'nowrap',
  fontWeight: '600',
});

export default function VerifyEmailPageForm({ error }: { error?: string }) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const portalName = usePortalName();
  const clientBC = securityUtils.getPortalClientBC(portalName || '');
  const loggedInUser = useAppSelector(selectLoggedInUserInfo);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setErrorMessage(error || '');
  }, [error]);

  const handleResendVerificationEmail = () => {
    const callbackUrl = window.location.href.split('?')[0];
    const logoUrl = securityUtils.getPortalLogoURL(portalName || '');
    userService
      .sendVerificationEmail(clientBC, callbackUrl, logoUrl)
      .subscribe({
        next() {
          setErrorMessage('');
        },
        error(err) {
          if (err.message) {
            setErrorMessage(String(err.message) || '');
          } else {
            setErrorMessage(String(err) || '');
          }
        },
      });
  };

  const logout = () => {
    authService.logout().then(() => {
      // This set timeout is required to give the logout time to work fully in AWS
      setTimeout(() => {
        dispatch(resetUserState());
        window.location.href = '/login';
      });
    });
  };

  return (
    <Root className={classes.container}>
      <Box className={classes.formPage}>
        <Box
          component="img"
          src="/email_large.svg"
          alt={t('member.verifyEmailForm.emailIcon')}
          className={classes.largeIcon}
        />
        <Typography className={classes.title} variant="h3">
          {t('member.verifyEmailForm.checkEmail')}
        </Typography>
        <Typography className={classes.subtitle} variant="body1">
          <Trans
            i18nKey="member.verifyEmailForm.checkEmailMessage"
            values={{ email: loggedInUser?.emailAddress || '' }}
            components={{ strong: <StyledBox component="span" /> }}
          />
        </Typography>
        {errorMessage && (
          <Typography
            variant="body2"
            color="error"
            className={classes.inlineError}>
            {errorMessage}
          </Typography>
        )}{' '}
        <Button
          color="primary"
          variant="contained"
          size="large"
          className={classes.next}
          type="submit"
          onClick={handleResendVerificationEmail}>
          {t('member.verifyEmailForm.resendEmail')}
        </Button>
      </Box>
      <Link onClick={logout} className={classes.link}>
        {t('member.backToLogin')}
      </Link>
    </Root>
  );
}
